(function () {
  "use strict";

  var TEMPLATE_SELECTOR = "[data-video-template]";
  var LOADING_CLASS = "is-loading";
  var VIDEO_ID_ATTR = "data-video-id";

  if (customElements.get("video-background")) return;

  customElements.define(
    "video-background",
    class extends HTMLElement {
      constructor() {
        super();
        this.resumeOnInteraction = this.resumeOnInteraction.bind(this);
        this.attemptPlay = this.attemptPlay.bind(this);
      }

      connectedCallback() {
        this.videoId = this.getAttribute(VIDEO_ID_ATTR);
        this.videoTemplate = this.querySelector(TEMPLATE_SELECTOR);
        this.video = null;

        if (!this.videoId || !this.videoTemplate) return;

        // An above-the-fold hero renders straight away so playback begins with the
        // page load instead of waiting on an intersection callback.
        if (this.isInViewport()) {
          this.renderVideo();
        } else {
          this.observeForRender();
        }
      }

      isInViewport() {
        var rect = this.getBoundingClientRect();
        if (!rect.height) return false; // Layout isn't settled yet; let the observer decide.
        return rect.top < window.innerHeight && rect.bottom > 0;
      }

      observeForRender() {
        this.videoTemplateObserver = new IntersectionObserver(
          function (entries, observer) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              entry.target.renderVideo();
            });
          },
          {
            root: null,
            rootMargin: "300px",
            threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
          }
        );
        this.videoTemplateObserver.observe(this);
      }

      renderVideo() {
        if (this.video) return;

        this.innerHTML = this.videoTemplate.innerHTML;
        this.classList.remove(LOADING_CLASS);
        this.video = this.querySelector("video");

        if (!this.video) return;

        // Safari on iOS only allows inline autoplay when muted and playsinline are
        // set as PROPERTIES on the element. Markup attributes alone aren't reliably
        // honoured on a node injected with innerHTML, and when they aren't, iOS
        // refuses the autoplay and draws its own play button over the poster.
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.playsInline = true;
        this.video.setAttribute("muted", "");
        this.video.setAttribute("playsinline", "");
        this.video.setAttribute("preload", "auto");

        // A first play() is often refused while the file is still buffering, so try
        // again as soon as there are frames available to show.
        this.video.addEventListener("loadeddata", this.attemptPlay);
        this.video.addEventListener("canplay", this.attemptPlay);

        this.observeVideoPlayToggle();
        this.video.load();
        this.attemptPlay();
      }

      attemptPlay() {
        if (!this.video || !this.video.paused) return;

        var playPromise = this.video.play();
        if (!playPromise || typeof playPromise.catch !== "function") return;

        var self = this;
        playPromise.catch(function () {
          // Low Power Mode and Low Data Mode block autoplay outright, so the next
          // tap or scroll is the earliest point playback can legally start.
          document.addEventListener("click", self.resumeOnInteraction, {
            passive: true,
            once: true
          });
          document.addEventListener("touchstart", self.resumeOnInteraction, {
            passive: true,
            once: true
          });
        });
      }

      resumeOnInteraction() {
        if (!this.video) return;
        var playPromise = this.video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(function () {});
        }
      }

      observeVideoPlayToggle() {
        if (!this.video) return;

        var self = this;
        this.videoPlayObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                self.attemptPlay();
              } else if (self.video && typeof self.video.pause === "function") {
                self.video.pause();
              }
            });
          },
          { rootMargin: "0px", threshold: [0, 1] }
        );
        this.videoPlayObserver.observe(this.video);
      }

      disconnectedCallback() {
        if (this.videoTemplateObserver) this.videoTemplateObserver.disconnect();
        if (this.videoPlayObserver) this.videoPlayObserver.disconnect();
        document.removeEventListener("click", this.resumeOnInteraction);
        document.removeEventListener("touchstart", this.resumeOnInteraction);
      }
    }
  );
})();